let socket = null;
let reconnectTimer = null;
let reconnectAttempts = 0;
const reconnectDelays = [300, 1200, 2500]; // ~4s total
let firstDisconnectAt = null;
const MAX_RECONNECT_WINDOW = 30000; // 30s
function clearReconnectTimers() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}
import { setSocketActivo } from '@/store/socket.js';

function buildWsUrl(token) {
  const base = process.env.VUE_APP_WEBSOCKET_URL; 
  if (!base) {
    throw new Error("VUE_APP_WEBSOCKET_URL no está definido");
  }

  const url = new URL(base, window?.location?.origin || undefined);

  if (window?.location?.protocol === "https:" && url.protocol !== "wss:") {
    url.protocol = "wss:";
  }

  // Adjunta el token
  url.searchParams.set("token", token);
  return url.toString();
}

export function connectWebSocket(token, onMessage, onClose, store, router) {
  if (socket && socket.readyState === WebSocket.OPEN) {
    return socket;
  }
  if (socket && socket.readyState === WebSocket.CONNECTING) {
    return socket;
  }
  if (socket) {
    socket.close(4000, "Reiniciando conexión");
  }

  let wsUrl;
  try {
    wsUrl = buildWsUrl(token);
  } catch (e) {
    setSocketActivo(false);
    if (typeof window !== 'undefined') {
      window.socketActivo = false;
      try {
        window.dispatchEvent(new CustomEvent("ws-disconnected", { detail: { reason: e?.message || "URL de WebSocket inválida" } }));
      } catch (_) { /* noop */ }
    }
    if (onClose) {
      try { onClose(e); } catch (_) { /* noop */ }
    }
    return null;
  }

  try {
    socket = new WebSocket(wsUrl);
  } catch (e) {
    setSocketActivo(false);
    if (typeof window !== 'undefined') {
      window.socketActivo = false;
      try {
        window.dispatchEvent(new CustomEvent("ws-disconnected", { detail: { reason: "No se pudo crear la conexión WebSocket" } }));
      } catch (ev) { /* noop */ }
    }
    if (onClose) {
      try { onClose(e); } catch (_) { /* noop */ }
    }
    return null;
  }

  socket.onopen = () => {
    setSocketActivo(true);
    if (typeof window !== 'undefined') window.socketActivo = true;
    reconnectAttempts = 0;
    clearReconnectTimers();
    firstDisconnectAt = null;
  };

  const handlers = {
    session_kicked: async (datos) => {
      try {
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("session-kicked", {
              detail: { reason: datos?.reason || "Sesión finalizada" },
            }),
          );
        }
      } catch (e) { /* noop */ }

      if (socket) {
        socket.onclose = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.close(4001, "Sesión expulsada por otro dispositivo");
        socket = null;
      }
      setSocketActivo(false);

      try {
        await store.dispatch("Auth/logout");
        try {
            const currentPath = router && router.currentRoute ? router.currentRoute.path : null;
            const query = { reason: 'kicked' };
            if (router && typeof router.push === "function") {
              if (currentPath !== "/login") router.push({ path: '/login', query });
            } else if (typeof window !== "undefined") {
              const q = new URLSearchParams(query).toString();
              window.location.href = `/login?${q}`;
            }
          } catch (e) {
            if (typeof window !== "undefined") window.location.href = "/login?reason=kicked";
          }
      } catch (e) {/*noop*/}
    },
    chat_message: () => {
    },
    ping: () => {
    },
    default: (data) => {
      // Despachar cualquier mensaje desconocido como evento global para que los componentes lo consuman
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('ws-message', { detail: data }));
        }
      } catch { /* noop */ }
    },
  };

  socket.onmessage = (event) => {
    const data = JSON.parse(event.data);
    if (onMessage) onMessage(data);
    const handler = handlers[data.type] || handlers.default;
    handler(data);
  };

  socket.onclose = async (event) => {
    setSocketActivo(false);
    if (typeof window !== 'undefined') window.socketActivo = false;
    // Cierres que requieren logout y redirección inmediata
    const immediateLogoutCodes = [4001, 4002, 4003]; // inválido/expulsado/logout/inactividad desde servidor
    if (immediateLogoutCodes.includes(event.code)) {
      try {
        clearReconnectTimers();
        reconnectAttempts = 0;
        if (socket) {
          try {
            socket.onclose = null;
            socket.onmessage = null;
            socket.onerror = null;
            socket.close(event.code, event.reason || 'Cierre por servidor');
          } catch (e) { /* noop */ }
          socket = null;
        }
      } catch (e) { /* noop */ }

      try { if (store && typeof store.dispatch === 'function') await store.dispatch('Auth/logout'); } catch (e) { /* noop */ }

      // Notificar desconexión a la app para posibles modales y flujos
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('ws-disconnected', { detail: { code: event.code, reason: event.reason } }));
        }
      } catch (e) { /* noop */ }

      // Redirigir a login con motivo para que el Login muestre su modal
      try {
        const currentPath = router && router.currentRoute ? router.currentRoute.path : null;
        const query = { reason: 'ws_down' };
        if (router && typeof router.push === 'function') {
          if (currentPath !== '/login') router.push({ path: '/login', query });
        } else if (typeof window !== 'undefined') {
          const q = new URLSearchParams(query).toString();
          window.location.href = `/login?${q}`;
        }
      } catch (e) { /* noop */ }

      if (onClose) try { onClose(event); } catch (_) { /* noop */ }
      return; // Evitar lógica de reconexión para estos códigos
    }
    // Intentar reconectar automáticamente salvo cierres intencionales o expulsión
    const intentionalClose = event.code === 1000; // cierre limpio desde cliente
    const kicked = event.code === 4001; // expulsado por otro dispositivo
    if (!intentionalClose && !kicked) {
      if (!firstDisconnectAt) firstDisconnectAt = Date.now();
      const elapsed = Date.now() - firstDisconnectAt;
      const token = (typeof sessionStorage !== 'undefined') ? sessionStorage.token : null;

      if (elapsed >= MAX_RECONNECT_WINDOW) {
        // Superó ventana de reconexión: forzar logout y limpiar
        try {
          // Evitar nuevos intentos
          clearReconnectTimers();
          reconnectAttempts = 0;
          // Cerrar y limpiar socket
          if (socket) {
            try {
              socket.onclose = null;
              socket.onmessage = null;
              socket.onerror = null;
              socket.close(4002, 'Timeout de reconexión excedido');
            } catch (e) { /* noop */ }
            socket = null;
          }
          setSocketActivo(false);
          if (typeof window !== 'undefined') window.socketActivo = false;

          // Llamar logout backend y limpiar sesión
          try { if (store && typeof store.dispatch === 'function') await store.dispatch('Auth/logout'); } catch (e) { /* noop */ }
          try {
            const currentPath = router && router.currentRoute ? router.currentRoute.path : null;
            if (router && typeof router.push === 'function') {
              if (currentPath !== '/login') router.push({ path: '/login', query: { reason: 'ws_timeout' } });
            } else if (typeof window !== 'undefined') {
              window.location.href = '/login?reason=ws_timeout';
            }
          } catch (e) {
            if (typeof window !== 'undefined') window.location.href = '/login?reason=ws_timeout';
          }
        } catch (e) {/*noop*/}
        return; // no programar más reintentos
      }

      if (token) {
        const delay = reconnectDelays[Math.min(reconnectAttempts, reconnectDelays.length - 1)];
        clearReconnectTimers();
        reconnectTimer = setTimeout(() => {
          reconnectAttempts += 1;
          try {
            connectWebSocket(token, onMessage, onClose, store, router);
          } catch (e) {/*noop*/}
        }, delay);
      }
    }
    try {
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("ws-disconnected", { detail: { code: event.code, reason: event.reason } }),
        );
      }
    } catch (e) {/*noop*/}
    if (onClose) onClose(event);
  };

  socket.onerror = () => {
    setSocketActivo(false);
    if (typeof window !== "undefined") {
      window.socketActivo = false;
      try {
        window.dispatchEvent(new CustomEvent("ws-disconnected", { detail: { reason: "Error en WebSocket" } }));
      } catch (e) { /* noop */ }
    }
  };
}

export function sendMessage(payload) {
  if (socket && socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(payload));
  }
}

export function disconnectWebSocket() {
  if (socket) {
    socket.onclose = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.close(1000, "Cliente cerró la conexión");
    socket = null;
    setSocketActivo(false);
    // Cancelar reconexiones pendientes
    clearReconnectTimers();
    reconnectAttempts = 0;
  }
}
